import {
    Text,
    Breakpoints,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
    CTA,
    Container,
    Image,
    ThemeProvider,
} from '@volkswagen-onehub/components-core'
import {SizeEnum} from '@volkswagen-onehub/tokens'
import styled from 'styled-components'
import React from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {Back, Info} from '@volkswagen-onehub/icons-core'
import {openLegalNotes} from '../../shared/utilities'
import {SingleSimulation} from '../../types/SingleSimulation'
import isEmpty from 'lodash.isempty'
import {ModelName} from '../components/ModelName'
import {SimulationData} from '../components/SimulationData'
import {useFeatureServices} from '../../use-feature-services'
import {vehicleTracking} from '../../shared/trackingServiceHelper'
import {Global} from '../../shared/variables'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'

const StageContainer = styled.div`
    position: relative;
    padding-top: ${(props) => props.theme.size.static750};
    padding-right: ${(props) => props.theme.size.grid002};
    padding-left: ${(props) => props.theme.size.grid002};
    background: ${({theme}) => theme.colors.gradient.left};

    @media (min-width: ${Breakpoints.b960}px) {
        padding-right: ${(props) => props.theme.size.grid001};
        padding-left: ${(props) => props.theme.size.grid001};
        padding-bottom: ${(props) => props.theme.size.dynamic0050};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        padding-right: ${(props) => props.theme.size.grid002};
        padding-left: ${(props) => props.theme.size.grid002};
        padding-bottom: ${(props) => props.theme.size.dynamic0150};
    }

    @media (min-width: ${Breakpoints.b2560}px) {
        padding-left: ${(props) => props.theme.size.grid006};
        padding-right: ${(props) => props.theme.size.grid006};
    }
`

const ContentWrapper = styled.div`
    @media (min-width: ${Breakpoints.b960}px) {
        display: grid;
        grid-template-columns: 2fr 3fr;
    }
`

const StageImage = styled(Image)`
    transform: translateY(1.5rem);
    width: 100%;

    @media (min-width: ${Breakpoints.b560}px) {
        transform: translateY(4rem);
    }

    @media (min-width: ${Breakpoints.b960}px) {
        transform: translateY(9rem);
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        transform: translateY(10rem);
    }
`

interface DealerFormStageProps {
    promoData: SingleSimulation
    image: string
}

export function DealerFormStage(props: DealerFormStageProps): JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const {'s2:history': historyService} = useFeatureServices() as any

    const promoIndex = useSelector(
        (state: RootState) => state.dealerFormData.promoIndex
    )

    return (
        <ThemeProvider theme="inverted">
            {!isEmpty(props.promoData) ? (
                <StageContainer>
                    <CTA
                        tag="button"
                        emphasis="tertiary"
                        aria-label={t('cta.back')}
                        icon={<Back aria-hidden />}
                        iconPosition="start"
                        onClick={() => {
                            historyService.history.push('/')
                            vehicleTracking(
                                Global.LINK,
                                props.promoData.model,
                                props.promoData.version,
                                props.promoData.monthly_payment,
                                window.location.toString(),
                                t('cta.back'),
                                `popup-${props.promoData.id}`
                            )
                        }}
                    >
                        {t('cta.back')}
                    </CTA>

                    {/*Add comment to trigger pipeline*/}

                    <Text
                        tag={TextTag.h1}
                        appearance={TokenTextAppearance.headline500}
                    >
                        <Trans
                            i18nKey="dealer-form.stage-title"
                            ns="fa-flotte"
                            components={{strong: <strong />}}
                        />
                    </Text>

                    <ContentWrapper>
                        <Container
                            padding={{
                                top: {
                                    [Breakpoints.default]: SizeEnum.static515,
                                },
                                left: {
                                    [Breakpoints.b960]: SizeEnum.grid001,
                                },
                                right: {
                                    [Breakpoints.b960]: SizeEnum.grid001,
                                },
                            }}
                            wrap="always"
                        >
                            <ModelName
                                model={props.promoData.model}
                                bigger={true}
                                tag={TextTag.h4}
                                color={TokenTextColor.primary}
                            />

                            <Container
                                gutter={SizeEnum.static250}
                                padding={{
                                    top: {
                                        [Breakpoints.default]:
                                            SizeEnum.static400,
                                    },
                                }}
                                wrap="always"
                            >
                                <SimulationData
                                    promoData={props.promoData}
                                    modelName={props.promoData.model}
                                    color={TokenTextColor.primary}
                                />

                                <CTA
                                    tag="button"
                                    emphasis="tertiary"
                                    onClick={(e: React.MouseEvent): void => {
                                        e.preventDefault()
                                        openLegalNotes(
                                            props.promoData.legal_notes
                                        )
                                        vehicleTracking(
                                            Global.LINK,
                                            props.promoData.model,
                                            props.promoData.version,
                                            props.promoData.monthly_payment,
                                            window.location.toString(),
                                            t('cta.legal'),
                                            `${props.promoData.id}-${promoIndex}`
                                        )
                                    }}
                                    aria-label={t('cta.legal')}
                                    icon={<Info aria-hidden />}
                                >
                                    {t('cta.legal')}
                                </CTA>
                            </Container>
                        </Container>
                        <StageImage
                            src={props.image}
                            alt={props.promoData.model}
                        />
                    </ContentWrapper>
                </StageContainer>
            ) : undefined}
        </ThemeProvider>
    )
}
